<template>
  <CRow>
    <CCol col>
      <CCard>
        <CCardHeader>
          <CRow class="justify-content-between px-3">
            <h5 class="mb-0">Affiliates</h5>
            <div class="d-flex">
              <CSelect
                style="width: 180px"
                class="mx-2 my-0"
                :value.sync="search.type"
                :options="search.options"
              />
              <CInput
                style="width: 360px"
                class="my-0 px-0"
                :placeholder="`Search ${search.placeholder}`"
                :value.sync="search.value"
                @keyup.enter="onSearch"
              >
                <template #append>
                  <CButton color="primary" @click="onSearch">Search</CButton>
                </template>
              </CInput>
            </div>
          </CRow>
        </CCardHeader>
        <CCardBody>
          <CDataTable
            hover
            striped
            border
            :loading="loading"
            :items-per-page-select="false"
            :items="affiliates"
            :fields="fields"
            :items-per-page="pagination.limit"
            :pagination="false"
          >
            <td slot="status" slot-scope="{ item }">
              <CBadge :color="getUserBadge(item.status)">
                {{ item.status }}
              </CBadge>
            </td>
            <td slot="id" slot-scope="{ item }">
              <CLink :href="'#/consumers/' + item.id">{{ item.id }}</CLink>
            </td>
            <td slot="phone_number" slot-scope="{ item }">
              {{ item.phone_number || '-' }}
            </td>
            <td slot="referral_code" slot-scope="{ item }">
              {{ item.referral_code || '-' }}
            </td>
            <td slot="credit_balance" slot-scope="{ item }">
              {{ creditAmount(item) }}
            </td>
            <td slot="credit_expires_at" slot-scope="{ item }">
              <span v-if="creditExpiresAt(item)">
                {{ creditExpiresAt(item) | moment(datetimeFormat) }}
              </span>
              <span v-else>-</span>
            </td>
            <td slot="actions" slot-scope="{ item }">
              <CButton
                size="sm"
                color="primary"
                :disabled="
                  creditAmount(item) <= 0 || payoutLoadingId === item.id
                "
                @click="openPayout(item)"
              >
                <CSpinner v-if="payoutLoadingId === item.id" size="sm" />
                Payout
              </CButton>
            </td>
          </CDataTable>

          <MyPagination
            :limit="pagination.limit"
            :total="pagination.total"
            :activePage="pagination.page"
            @changePage="onChangePage"
            @changePageSize="onChangePageSize"
          />
        </CCardBody>
      </CCard>

      <CModal
        title="Confirm credits payout"
        :show.sync="showPayoutModal"
        :closeOnBackdrop="false"
      >
        <p v-if="payoutTarget">
          Payout {{ creditAmount(payoutTarget) }} credits for consumer
          {{ payoutTarget.id }}? The credit balance will be adjusted to 0.
        </p>
        <template #footer>
          <CButton color="secondary" @click="closePayout">Cancel</CButton>
          <CButton
            color="primary"
            :disabled="Boolean(payoutLoadingId)"
            @click="confirmPayout"
          >
            Confirm payout
          </CButton>
        </template>
      </CModal>
    </CCol>
  </CRow>
</template>

<script>
import UserProxy from '@/proxies/user.proxy';
import ConsumerProxy from '@/proxies/consumer.proxy';
import MyPagination from '@/components/MyPagination.vue';
import { UserBadge } from '@/utils/utils';
import { Common } from '@/constants';

export default {
  name: 'Affiliates',
  components: { MyPagination },
  data() {
    return {
      datetimeFormat: Common.datetimeFormat,
      loading: false,
      affiliates: [],
      fields: [
        { key: 'status', label: 'Status' },
        { key: 'id', label: 'UserID' },
        { key: 'name', label: 'Name' },
        { key: 'phone_number', label: 'Phone number' },
        { key: 'referral_code', label: 'Referral code' },
        { key: 'credit_balance', label: 'Credit Balance' },
        { key: 'credit_expires_at', label: 'Credit ExpireAt' },
        { key: 'actions', label: '' },
      ],
      pagination: {
        page: 1,
        limit: 25,
        total: 0,
      },
      search: {
        type: 'name',
        value: '',
        placeholder: 'Name',
        options: [
          { value: 'name', label: 'Name' },
          { value: 'id', label: 'UserID' },
          { value: 'phone_number', label: 'Phone number' },
          { value: 'referral_code', label: 'Referral code' },
        ],
      },
      payoutTarget: null,
      payoutLoadingId: null,
      showPayoutModal: false,
    };
  },
  watch: {
    'search.type'() {
      const option = this.search.options.find(
        (item) => item.value === this.search.type
      );
      this.search.placeholder = option ? option.label : 'Affiliate';
    },
  },
  mounted() {
    this.fetchAffiliates();
  },
  methods: {
    getUserBadge(status) {
      return UserBadge(status);
    },
    creditAmount(item) {
      if (!item || !item.credit_balance) {
        return 0;
      }
      return Number(item.credit_balance.total_credit_amount) || 0;
    },
    creditExpiresAt(item) {
      return item && item.credit_balance
        ? item.credit_balance.expires_at
        : null;
    },
    async fetchAffiliates() {
      const query = {
        limit: this.pagination.limit,
        page: this.pagination.page,
      };
      if (this.search.value) {
        query[this.search.type] = this.search.value;
      }

      this.loading = true;
      try {
        const { data } = await UserProxy.affiliatesPaging(query);
        const payload = data || {};
        this.affiliates = Array.isArray(payload.rows) ? payload.rows : [];
        this.pagination.page = Number(payload.page) || query.page;
        this.pagination.limit = Number(payload.limit) || query.limit;
        this.pagination.total = Number(payload.total_rows) || 0;
      } catch (error) {
        this.$toast.error('Failed to load affiliates');
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.pagination.page = 1;
      return this.fetchAffiliates();
    },
    onChangePage(page) {
      this.pagination.page = page;
      return this.fetchAffiliates();
    },
    onChangePageSize(limit) {
      this.pagination.limit = limit;
      this.pagination.page = 1;
      return this.fetchAffiliates();
    },
    openPayout(item) {
      if (this.creditAmount(item) <= 0) {
        return;
      }
      this.payoutTarget = item;
      this.showPayoutModal = true;
    },
    closePayout() {
      if (this.payoutLoadingId) {
        return;
      }
      this.showPayoutModal = false;
      this.payoutTarget = null;
    },
    async confirmPayout() {
      if (!this.payoutTarget || this.payoutLoadingId) {
        return;
      }
      const consumerID = this.payoutTarget.id;
      this.payoutLoadingId = consumerID;
      try {
        await ConsumerProxy.adminPayoutCredit(consumerID);
        this.showPayoutModal = false;
        this.payoutTarget = null;
        this.$toast.success('Credits payout successfully');
        await this.fetchAffiliates();
      } catch (error) {
        this.$toast.error('Failed to payout credits');
      } finally {
        this.payoutLoadingId = null;
      }
    },
  },
};
</script>
